import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Application, BankStatementAccount, BankStatementData, BankStatementDecisioningSelection, BankStatementTransactionRow, BsaCalculator, CreateApplicationNoteFn, GetBsaCalculatorFn, GetBsaExcludedLenderListFn, GetBsaLenderListFn, GetDscrCalculatorHistoryFn, GetDscrCalculatorValueFn, isInternalUser, RemoveApplicationNoteByNoteIdFn, restoreBsaConfirmedTransactions, SaveBsaCalculatorFn, UpdateDscrCalculatorValueFn, User } from '@portal-workspace/grow-shared-library';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LooseCurrencyPipe } from '../../pipes/loose-currency.pipe';
import { DayEndBalanceChartComponent } from './day-end-balance-chart.component';
import { DecisionMetricsComponent } from './decision-metrics.component';
import { TransactionSummaryComponent } from './transaction-summary.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';
import { BankStatementOverviewComponent } from './bank-statement-overview.component';
import { BankStatementLoanPaymentComponent } from './bank-statement-loan-payment.component';
import { BankStatementIncomeComponent } from './bank-statement-income.component';
import { BankStatementDscrCalculatorComponent } from './bank-statement-dscr-calculator.component';
import { BankStatementAtoPaymentComponent } from './bank-statement-ato-payment.component';
import { Subscription } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { GetNotesByApplicationIdFn } from '../asset-listing-component/notes.component';
import { getUser } from '@portal-workspace/grow-ui-library';

export type BankStatementTab = { selectionType: BankStatementDecisioningSelection, name: string, index: number };

@Component({
  selector: 'bank-statement-decisioning',
  templateUrl: './bank-statement-decisioning.component.html',
  styleUrls: ['./bank-statement-decisioning.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatTabsModule, NgClass, BankStatementLoanPaymentComponent, BankStatementAtoPaymentComponent, BankStatementDscrCalculatorComponent, ExtendedModule, TransactionSummaryComponent, DecisionMetricsComponent, DayEndBalanceChartComponent, LooseCurrencyPipe, BankStatementOverviewComponent, BankStatementIncomeComponent, MatMenuModule, MatIconButton]
})
export class BankStatementDecisioningComponent implements OnInit, OnChanges {
  @Input({ required: true }) data!: BankStatementData;
  @Input({ required: true }) getBsaLenderListFn!: GetBsaLenderListFn;
  @Input({ required: true }) getBsaExcludedLenderListFn!: GetBsaExcludedLenderListFn;
  @Input({ required: true }) getDscrCalculatorValueFn!: GetDscrCalculatorValueFn;
  @Input({ required: true }) updateDscrCalculatorValueFn!: UpdateDscrCalculatorValueFn;
  @Input({ required: true }) application!: Application;
  @Input({ required: true }) saveBsaCalculatorFn!: SaveBsaCalculatorFn;
  @Input({ required: true }) getBsaCalculatorFn!: GetBsaCalculatorFn;
  @Input({ required: true }) getDscrCalculatorHistoryFn!: GetDscrCalculatorHistoryFn;
  @Input() savedCalculator: BsaCalculator | null = null;
  @Input() getNotesByApplicationIdFn!: GetNotesByApplicationIdFn;
  @Input() createApplicationNoteFn!: CreateApplicationNoteFn;
  @Input() removeApplicationNoteByNoteIdFn!: RemoveApplicationNoteByNoteIdFn;
  @Input() selectedAccounts!: string[];
  @Input() requestHistoricalDataFn?: () => void;
  @ViewChild('bsTabs') tabGroup!: MatTabGroup;

  subscriptions: Subscription[] = [];
  currentSection: BankStatementDecisioningSelection = 'overview';
  tabs: BankStatementTab[] = [];

  dataSource: BankStatementAccount[] = [];
  columnsToDisplay = ['icon', 'BankName', 'BSBAccNumber', 'AccHolder', 'AccName', 'version', 'currentBalance', 'TotalCredit', 'LoanCreditFlag'];
  expandedElement!: BankStatementAccount | null;
  
  // Version management properties
  groupedAccounts: Map<string, BankStatementAccount[]> = new Map();
  selectedVersions: Map<string, BankStatementAccount> = new Map();
  displayedAccounts: BankStatementAccount[] = [];
  filteredData!: BankStatementData;

  confirmLoanPayment: boolean = false;
  confirmIncome: boolean = false;
  confirmAtoPayment: boolean = false;
  loanPaymentTransactions: BankStatementTransactionRow[] = [];
  incomeTransactions: BankStatementTransactionRow[] = [];
  atoPaymentTransactions: BankStatementTransactionRow[] = [];
  loggedInUser: User | null = getUser();
  isInternalUser = isInternalUser(this.loggedInUser)
  constructor(
    private applicationDialogService: ApplicationDialogService,
    private toastService: PortalHotToastService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const restored = restoreBsaConfirmedTransactions(this.savedCalculator);
    this.loanPaymentTransactions = restored.loanPaymentTransactions;
    this.incomeTransactions = restored.incomeTransactions;
    this.atoPaymentTransactions = restored.atoPaymentTransactions;
    this.confirmLoanPayment = restored.confirmLoanPayment;
    this.confirmIncome = restored.confirmIncome;
    this.confirmAtoPayment = restored.confirmAtoPayment;

    this.initializeAccountVersions();

    this.tabs = [
      { selectionType: 'overview', name: 'Overview', index: 0 },
    ]
    if (this.isInternalUser) {
      this.tabs.push(
      { selectionType: 'loan payment', name: 'Loan Payments', index: 1 },
      { selectionType: 'income', name: 'Income', index: 2 },
      { selectionType: 'ato payment', name: 'ATO Payments', index: 3 },
      {selectionType: 'dscr calculator',name: 'DSCR Calculator',index: 4});
    }
  }

  async setCurrentSelection(tabIndex: number) {
    const tabFound = this.tabs.find(t => t.index == tabIndex);
    const selection = tabFound ? tabFound.selectionType : 'overview' /* default selection if not found */;
    this.currentSection = selection as BankStatementDecisioningSelection;
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.initializeAccountVersions();
    }
  }
  
  private initializeAccountVersions(): void {
    if (!this.data?.accounts) {
      this.dataSource = [];
      return;
    }
    
    // Group accounts by bsbAccNumber
    this.groupAccountsByIdentifier();
    
    // Prefer a previously saved version when that account id is still selected
    this.displayedAccounts = [];
    this.groupedAccounts.forEach((versions, key) => {
      const savedVersion = versions.find(v => this.selectedAccounts.includes(v.id));
      const latestVersion = versions.find(v => v.isLatestVersion) || versions[0];
      const displayedVersion = savedVersion || latestVersion;
      this.selectedVersions.set(key, displayedVersion);
      this.displayedAccounts.push(displayedVersion);
    });
    
    this.updateFilteredData();
    this.dataSource = this.displayedAccounts;
    this.cdr.detectChanges();
  }
  
  private updateFilteredData(): void {
    this.filteredData = {
      ...this.data,
      accounts: [...this.displayedAccounts]
    };
  }
  
  private groupAccountsByIdentifier(): void {
    this.groupedAccounts.clear();
    
    if (!this.data?.accounts) return;
    
    this.data.accounts.forEach(account => {
      const key = this.getAccountKey(account);
      
      if (!this.groupedAccounts.has(key)) {
        this.groupedAccounts.set(key, []);
      }
      this.groupedAccounts.get(key)!.push(account);
    });
    
    // Sort versions by loadDate (newest first)
    this.groupedAccounts.forEach((versions, key) => {
      versions.sort((a, b) => {
        if (!a.loadDate) return 1;
        if (!b.loadDate) return -1;
        return new Date(b.loadDate).getTime() - new Date(a.loadDate).getTime();
      });
    });
  }
  
  getAccountKey(account: BankStatementAccount): string {
    return account.bsbAccNumber || account.id;
  }
  
  getVersionsForAccount(account: BankStatementAccount): BankStatementAccount[] {
    const key = this.getAccountKey(account);
    return this.groupedAccounts.get(key) || [account];
  }
  
  hasMultipleVersions(account: BankStatementAccount): boolean {
    const versions = this.getVersionsForAccount(account);
    return versions.length > 1;
  }
  
  selectVersion(currentAccount: BankStatementAccount, selectedVersion: BankStatementAccount): void {
    if (!currentAccount || !selectedVersion) {
      console.error('Invalid account or version provided to selectVersion');
      return;
    }
    
    const key = this.getAccountKey(currentAccount);
    const wasExpanded = this.expandedElement && this.getAccountKey(this.expandedElement as any) === key;
    
    this.selectedVersions.set(key, selectedVersion);
    
    // Update selectedAccounts array if the current account is selected
    const oldAccountId = currentAccount.id;
    const newAccountId = selectedVersion.id;
    
    if (this.selectedAccounts.includes(oldAccountId)) {
      // Replace old account ID with new version's ID
      this.selectedAccounts = this.selectedAccounts.map(id => 
        id === oldAccountId ? newAccountId : id
      );
      
      // Save updated selection if saveBsaCalculatorFn is available
      if (this.saveBsaCalculatorFn && this.application?.ApplicationId) {
        this.subscriptions.push(
          this.saveBsaCalculatorFn({
            applicationId: this.application.ApplicationId,
            data: {
              selectedAccounts: this.selectedAccounts
            },
            saveDscrCalculator: false,
          }).subscribe()
        );
      }
    }
    
    // Update the dataSource with the selected version
    const index = this.displayedAccounts.findIndex(acc => this.getAccountKey(acc) === key);
    if (index !== -1) {
      this.displayedAccounts = [
        ...this.displayedAccounts.slice(0, index),
        selectedVersion,
        ...this.displayedAccounts.slice(index + 1)
      ];
      
      this.updateFilteredData();
      this.dataSource = [...this.displayedAccounts]; // Trigger change detection
      
      // If this account was expanded, update the expanded element to the new version
      if (wasExpanded) {
        this.expandedElement = selectedVersion as any;
      }
      
      this.cdr.detectChanges();
    }
  }
  
  formatUploadDate(dateString?: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  formatUploadTime(dateString?: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  }

  onSelectAccounts(selectedAccounts: string[]) {
    console.log('Selected accounts:', selectedAccounts);
    
    // Map selected account IDs to their currently selected versions
    const mappedSelectedAccounts: string[] = [];
    
    selectedAccounts.forEach(accountId => {
      // Find the account
      const account = this.data.accounts.find(acc => acc.id === accountId);
      if (account) {
        const key = this.getAccountKey(account);
        // Check if a different version is currently selected
        const selectedVersion = this.selectedVersions.get(key);
        if (selectedVersion) {
          mappedSelectedAccounts.push(selectedVersion.id);
        } else {
          mappedSelectedAccounts.push(accountId);
        }
      }
    });
    
    this.selectedAccounts = mappedSelectedAccounts;
    this.confirmLoanPayment = false;
    this.confirmIncome = false;
    this.confirmAtoPayment = false;
    this.loanPaymentTransactions = [];
    this.incomeTransactions = [];
    this.atoPaymentTransactions = [];

    this.subscriptions.push(
      this.saveBsaCalculatorFn({
        applicationId: this.application.ApplicationId,
        data: {
          selectedAccounts: this.selectedAccounts,
          loanPaymentTransactions: [],
          incomeTransactions: [],
          atoPaymentTransactions: [],
        },
        saveDscrCalculator: false,
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe(() => {
        this.tabGroup.selectedIndex = 0;
      })
    )
  }

  updataLoanPaymentTransactions(data: BankStatementTransactionRow[]) {
    this.loanPaymentTransactions = data;
    this.confirmLoanPayment = true;

    this.subscriptions.push(
      this.saveBsaCalculatorFn({
        applicationId: this.application.ApplicationId,
        data: {
          loanPaymentTransactions: this.loanPaymentTransactions
        },
        saveDscrCalculator: false,
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe(() => {
        this.tabGroup.selectedIndex = 2;
        this.cdr.detectChanges();
      })
    )

  }

  updateIncomeTransactions(data: BankStatementTransactionRow[]) {
    this.incomeTransactions = data;
    this.confirmIncome = true;

    this.subscriptions.push(
      this.saveBsaCalculatorFn({
        applicationId: this.application.ApplicationId,
        data: {
          incomeTransactions: this.incomeTransactions
        },
        saveDscrCalculator: false,
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe(() => {
        this.tabGroup.selectedIndex = 3;
        this.cdr.detectChanges();
      })
    )

  }

  updateAtoPaymentTransactions(data: BankStatementTransactionRow[]) {
    this.atoPaymentTransactions = data;
    this.confirmAtoPayment = true;

    this.subscriptions.push(
      this.saveBsaCalculatorFn({
        applicationId: this.application.ApplicationId,
        data: {
          atoPaymentTransactions: this.atoPaymentTransactions
        },
        saveDscrCalculator: false,
      }).pipe(
        this.toastService.spinnerObservable()
      ).subscribe(() => {
        this.tabGroup.selectedIndex = 4;
        this.cdr.detectChanges();
      })
    )

  }
}
